// Single switch point for the library / status / create backend contract. The
// frontend isolates the per-backend differences here so the rest of the app is
// agnostic; switching backends is a one-line change plus the response mappers.
//
//  'videos' (DEPLOYED — api.staging.valoi.ai) — CURRENT DEFAULT:
//      POST /api/v1/videos          -> { job_id, status }   (body user_id+text_input)
//      GET  /api/v1/videos          -> TaskApiItem[]         (scope via X-User-Id header)
//      GET  /api/v1/videos/{id}     -> TaskApiItem
//    Identity: derived server-side from the Bearer JWT (Depends(get_current_user));
//    the client sends ONLY the Authorization header (IDENTITY_FROM_JWT=true) — no
//    X-User-Id / body user_id. Responses are minimal today (task_id/status/download_url).
//
//  'video' (local Aman backend) — for local full-stack testing:
//      POST /api/v1/visualize            (Kling only)
//      GET  /video/library?user_id=...   -> { items: LibraryApiItem[] }
//      GET  /video/status/{id}           -> StatusApiResponse
//
// Switching is a one-word edit to LIBRARY_BACKEND; the adapters in libraryApi.ts
// and videoApi.ts read the active path/headers from here.
import type {
  GenerationMetadata,
  JobError,
  JobProgress,
  ProcessingStatus,
} from '@/shared/types';

export type LibraryBackend = 'videos' | 'video';

// `as LibraryBackend` keeps the union type so the helpers below can branch on it;
// switching backends is still a one-word edit ('videos' -> 'video').
export const LIBRARY_BACKEND = 'videos' as LibraryBackend;

// Identity source for the video endpoints. The backend now derives the user from
// the Bearer JWT (Depends(get_current_user)) and ignores any client-supplied id,
// so the client sends ONLY the Authorization header — no X-User-Id / body user_id.
// (Set back to false only if the backend reverts to client-supplied identity.)
export const IDENTITY_FROM_JWT = true;

// Mirrors the backend's JobProgress (app/datatypes.py). null once the job is
// terminal or before a story plan exists yet — every count is a query over data
// the pipeline already persists per character/scene, not new tracking.
export type JobProgressApi = {
  characters: { total: number; ready: number };
  keyframes: { total: number; ready: number };
  narration_ready: boolean;
  scenes: { total: number; completed: number; rendering: number; failed: number };
  eta_seconds?: number | null;
} | null;

// Shape of the /api/v1/videos items (and the local /video/library items mapped to
// the same names). `task_id`/`status` always exist; the rest are enrichments we
// requested from the backend (optional until they ship, so the mapper degrades
// gracefully in the meantime).
export type TaskApiItem = {
  task_id: string;
  status: string;
  download_url?: string | null;
  title?: string;
  text_input?: string;
  video_url?: string | null;
  created_at?: string;
  provider?: string;
  aspect_ratio?: string;
  // Quality mode ("storytelling" | "cinematic") and visual style, both plain
  // columns on the engine's jobs row. describeGeneration renders them in the
  // content page's meta row; before these were returned, `mode` was populated by
  // nothing and `style` survived only for a job submitted in this browser session.
  mode?: string;
  style?: string;
  /*
   * Seconds *per scene* as requested at submission (5 or 10) — a generation
   * setting, not the length of the finished video. The engine names it `duration`
   * on the jobs row and `scene_duration_seconds` in its admin serializer; only
   * the second name is honest. Map it to `metadata.duration`, never to
   * `durationSeconds`, which is what LibraryCard badges as a runtime.
   */
  duration?: number;
  /*
   * How long the finished video actually runs, ffprobed off the assembled file
   * at upload time and stored on job_metadata. THIS is what maps to
   * `durationSeconds`; `duration` above never does. Null for jobs finished
   * before the column existed and for anything still rendering, in which case
   * LibraryCard falls back to the loaded <video> element's own duration — which
   * the browse grid and hero have no way to do, so they simply badge nothing.
   */
  runtime_seconds?: number | null;
  prompt_list?: string[];
  thumbnail_url?: string | null;
  // Presigned URL to the backend-generated WebVTT subtitle track. Populated only
  // for completed storytelling jobs; null/absent for cinematic or in-flight jobs.
  vtt_url?: string | null;
  // Structured, UI-safe failure detail (TaskResponse.error) — present only when
  // status === 'failed'. Already flat (code/message/retryable), no snake_case
  // remap needed. See valoi_engine app/datatypes.py::ApiError.
  error?: JobError | null;
  progress?: JobProgressApi;
  // True when the job pauses for character-reference review (engine TaskResponse).
  review_references?: boolean;
  estimated_cost?: number | null;
  estimated_duration_seconds?: number | null;
};

// Library list path. For 'video', identity rides in the ?user_id= query (unless
// IDENTITY_FROM_JWT); for 'videos' it rides in the X-User-Id header (see
// identityHeaders), so no query is appended.
export const libraryPath = (userId?: string): string => {
  if (LIBRARY_BACKEND === 'videos') {
    return '/api/v1/videos';
  }
  const query = !IDENTITY_FROM_JWT && userId ? `?user_id=${encodeURIComponent(userId)}` : '';
  return `/video/library${query}`;
};

// Single-job status path.
export const statusPath = (jobId: string): string =>
  LIBRARY_BACKEND === 'videos' ? `/api/v1/videos/${jobId}` : `/video/status/${jobId}`;

// WebSocket status-stream path (videos backend only).
export const wsStatusPath = (jobId: string): string =>
  `/api/v1/videos/${jobId}/status/ws`;

// Resume path for a failed/incomplete job.
export const resumePath = (jobId: string): string =>
  LIBRARY_BACKEND === 'videos'
    ? `/api/v1/videos/${jobId}/resume`
    : `/api/v1/visualize/resume/${jobId}`;

// Cancel path for an in-flight job.
export const cancelPath = (jobId: string): string =>
  LIBRARY_BACKEND === 'videos'
    ? `/api/v1/videos/${jobId}/cancel`
    : `/api/v1/visualize/cancel/${jobId}`;

// Create (generate) path for the 'videos' backend. The 'video' backend uses the
// /api/v1/visualize* family (resolved in videoApi.ts), so this is videos-only.
export const CREATE_VIDEO_PATH = '/api/v1/videos';

// Per-request identity headers. Returns an empty object whenever identity comes
// from the Bearer JWT (the current 'videos' default, IDENTITY_FROM_JWT=true), for
// the legacy 'video' backend, or when no id is available — so callers can spread
// it unconditionally. Only the legacy client-supplied path emits X-User-Id.
export const identityHeaders = (userId?: string): Record<string, string> =>
  LIBRARY_BACKEND === 'videos' && !IDENTITY_FROM_JWT && userId
    ? { 'X-User-Id': userId }
    : {};

// Normalizes a backend status string into the app's ProcessingStatus, instead of
// blind-casting (`as ProcessingStatus`) an arbitrary string into the enum. Maps
// common provider/DB spellings. An unknown/empty/missing status defaults to
// 'processing' (still pending): the pollers stop only on completed/failed, so we
// keep polling until the backend reports an explicit terminal state rather than
// risk marking a genuinely in-progress job as failed. (Backend status vocabulary
// to be confirmed; extend the cases as needed.)
export function normalizeStatus(raw: string | null | undefined): ProcessingStatus {
  switch (raw?.toLowerCase()) {
    case 'completed':
    case 'ready':
    case 'succeeded':
    case 'success':
      return 'completed';
    case 'failed':
    case 'error':
      return 'failed';
    case 'cancelled':
    case 'canceled':
      return 'cancelled';
    case 'queued':
      return 'queued';
    case 'awaiting_confirmation':
      return 'awaiting_confirmation';
    case 'awaiting_reference_review':
      return 'awaiting_reference_review';
    case 'processing':
    case 'running':
    case 'in_progress':
    case 'pending':
    default:
      return 'processing';
  }
}

export function isTerminalStatus(status: ProcessingStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

/*
 * `mode` and `style` arrive as free strings from `jobs.mode` / `jobs.style`.
 * Narrow rather than blind-cast, for the same reason as normalizeStatus above —
 * and drop anything unrecognised instead of substituting a default, because the
 * only consumer is the content page's meta row, which is supposed to say what
 * the engine actually reported and nothing else. A style the UI has never heard
 * of should leave a gap, not print the wrong word confidently.
 */
const GENERATION_MODES = ['cinematic', 'storytelling', 'non-narrative'] as const;
const GENERATION_STYLES = ['storybook', 'photoreal', 'documentation'] as const;

export function normalizeMode(
  raw: string | null | undefined,
): GenerationMetadata['mode'] {
  return GENERATION_MODES.find((mode) => mode === raw);
}

export function normalizeStyle(
  raw: string | null | undefined,
): GenerationMetadata['style'] {
  return GENERATION_STYLES.find((style) => style === raw);
}

// snake_case (backend) -> camelCase (frontend). Shared by the REST poller and
// the WebSocket mapper so the two paths can't drift.
export function mapJobProgress(raw: JobProgressApi): JobProgress | undefined {
  if (!raw) return undefined;
  return {
    characters: raw.characters,
    keyframes: raw.keyframes,
    narrationReady: raw.narration_ready,
    scenes: raw.scenes,
    // Finite or absent — a backend that ships a string or NaN here must read
    // as "no estimate", not propagate into "~NaN min remaining".
    etaSeconds: typeof raw.eta_seconds === 'number' && Number.isFinite(raw.eta_seconds)
      ? raw.eta_seconds
      : undefined,
  };
}
